/**
 * Link Prefetcher
 * Prefetches product and collection links on hover (desktop) or when visible in viewport (mobile)
 * to make navigation feel instant
 */

class LinkPrefetcher {
  constructor(options = {}) {
    this.options = {
      selector: 'a[href*="/products"], a[href*="/collections"]', // Prefetch product and collection links
      hoverDelay: 100, // ms to wait before prefetching on hover (debounce)
      mobileIntersectionThreshold: 0.25, // % of element visible before prefetching
      maxPrefetchOnMobile: 5, // Limit mobile prefetches to save bandwidth
      maxPrefetchOnSlowConnection: 2, // Even more conservative on slow networks
      respectDataSaver: true, // Disable prefetch if user has data saver enabled
      respectSlowConnection: true, // Disable/limit prefetch on slow connections
      ...options
    };

    this.prefetchedUrls = new Set();
    this.hoverTimeouts = new WeakMap();
    this.observer = null;
    this.mobilePrefetchCount = 0;
    this.isMobile = this.detectMobile();
    this.shouldPrefetch = this.checkNetworkConditions();

    this.init();
  }

  detectMobile() {
    // Check if touch device or small screen
    return "ontouchstart" in window || window.innerWidth < 768;
  }

  /**
   * Check network conditions to determine if we should prefetch
   * Respects data saver mode and slow connections
   */
  checkNetworkConditions() {
    // Check if Network Information API is available
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;

    // Check for data saver mode
    if (this.options.respectDataSaver && connection?.saveData) {
      return false;
    }

    // Check for slow connection (2g, slow-2g)
    if (this.options.respectSlowConnection && connection?.effectiveType) {
      const slowConnections = ["slow-2g", "2g"];
      if (slowConnections.includes(connection.effectiveType)) {
        return false;
      }

      // On 3G, reduce prefetch count
      if (connection.effectiveType === "3g") {
        this.options.maxPrefetchOnMobile = Math.min(
          this.options.maxPrefetchOnMobile,
          this.options.maxPrefetchOnSlowConnection
        );
      }
    }

    return true;
  }

  /**
   * Get max prefetch count based on network conditions
   */
  getMaxPrefetchCount() {
    if (!this.shouldPrefetch) return 0;

    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;

    // On 3G or slower, use conservative limit
    if (connection?.effectiveType === "3g") {
      return this.options.maxPrefetchOnSlowConnection;
    }

    return this.options.maxPrefetchOnMobile;
  }

  init() {
    // Don't initialize if network conditions are poor
    if (!this.shouldPrefetch) {
      return;
    }

    if (this.isMobile) {
      this.initIntersectionObserver();
    } else {
      this.initHoverListeners();
    }
  }

  /**
   * Desktop: Add hover listeners for instant prefetching
   */
  initHoverListeners() {
    document.addEventListener(
      "mouseover",
      (e) => {
        const link = e.target.closest(this.options.selector);
        if (!link) return;

        const url = this.getCleanUrl(link.href);
        if (!url) {
          return;
        }
        if (this.prefetchedUrls.has(url)) return;

        // Small delay to avoid prefetching when user quickly scrolls past
        const timeout = setTimeout(() => {
          this.prefetch(url);
        }, this.options.hoverDelay);

        this.hoverTimeouts.set(link, timeout);
      },
      { passive: true, capture: true }
    );

    // Cancel prefetch if mouse leaves quickly
    document.addEventListener(
      "mouseout",
      (e) => {
        const link = e.target.closest(this.options.selector);
        if (!link) return;

        const timeout = this.hoverTimeouts.get(link);
        if (timeout) {
          clearTimeout(timeout);
          this.hoverTimeouts.delete(link);
        }
      },
      { passive: true, capture: true }
    );
  }

  /**
   * Mobile: Use Intersection Observer to prefetch visible links
   */
  initIntersectionObserver() {
    if (!("IntersectionObserver" in window)) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const currentMax = this.getMaxPrefetchCount();

          if (entry.isIntersecting && this.mobilePrefetchCount < currentMax) {
            const link = entry.target;
            const url = this.getCleanUrl(link.href);

            if (url && !this.prefetchedUrls.has(url)) {
              // Delay slightly to prioritize visible content loading
              setTimeout(() => {
                this.prefetch(url);
                this.mobilePrefetchCount++;
              }, 100);

              // Stop observing this link
              this.observer.unobserve(link);
            }
          }
        });
      },
      {
        rootMargin: "50px", // Start prefetching slightly before element is visible
        threshold: this.options.mobileIntersectionThreshold
      }
    );

    // Observe all product links
    this.observeLinks();

    // Re-observe when new content is added (for infinite scroll, filters, etc.)
    this.setupMutationObserver();
  }

  observeLinks() {
    const maxPrefetch = this.getMaxPrefetchCount();
    const links = document.querySelectorAll(this.options.selector);

    links.forEach((link) => {
      if (this.mobilePrefetchCount < maxPrefetch) {
        this.observer?.observe(link);
      }
    });
  }

  /**
   * Watch for new links being added to the page
   */
  setupMutationObserver() {
    if (!("MutationObserver" in window)) return;

    const mutationObserver = new MutationObserver((mutations) => {
      let shouldObserveLinks = false;

      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length > 0) {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1) {
              // Element node
              const hasProductLinks =
                node.matches?.(this.options.selector) || node.querySelector?.(this.options.selector);
              if (hasProductLinks) {
                shouldObserveLinks = true;
              }
            }
          });
        }
      });

      if (shouldObserveLinks) {
        this.observeLinks();
      }
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Get clean URL without query params or hash
   */
  getCleanUrl(href) {
    if (!href) return null;

    try {
      const url = new URL(href, window.location.origin);

      // Only prefetch same-origin URLs
      if (url.origin !== window.location.origin) return null;

      // Only prefetch product and collection pages (supports language subpaths like /en/products)
      const isProduct = url.pathname.includes("/products/");
      const isCollection = url.pathname.includes("/collections/");

      if (!isProduct && !isCollection) return null;

      // Return clean path without query params
      return url.pathname;
    } catch (e) {
      return null;
    }
  }

  /**
   * Actually prefetch the URL
   */
  prefetch(url) {
    if (this.prefetchedUrls.has(url)) {
      return;
    }

    // Mark as prefetched
    this.prefetchedUrls.add(url);

    // Check if already prefetched
    const existing = document.querySelector(`link[rel="prefetch"][href="${url}"]`);
    if (existing) {
      return;
    }

    // Create prefetch link
    const link = document.createElement("link");
    link.rel = "prefetch";
    link.href = url;
    link.as = "document";

    // Add to document
    document.head.appendChild(link);

    // Optional: Remove after some time to clean up DOM
    setTimeout(() => {
      link.remove();
    }, 30000); // Remove after 30 seconds
  }

  /**
   * Manually prefetch a URL
   */
  prefetchUrl(url) {
    const cleanUrl = this.getCleanUrl(url);
    if (cleanUrl) {
      this.prefetch(cleanUrl);
    }
  }

  /**
   * Destroy and clean up
   */
  destroy() {
    this.observer?.disconnect();
    this.prefetchedUrls.clear();
    this.hoverTimeouts = new WeakMap();
  }
}

// Auto-initialize and expose globally
document.addEventListener("DOMContentLoaded", () => {
  window.linkPrefetcher = new LinkPrefetcher();
});

// Export for manual control if needed
export default LinkPrefetcher;
